import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { MeetingListItem } from '../types/meeting'
import { Timestamp } from 'firebase/firestore'
import { markReviewSubmitted } from '../lib/meetingService'
import { createReview } from '../lib/reviewService'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'

interface ReviewModalProps {
  open: boolean
  onClose: () => void
  meeting: MeetingListItem
  currentUserId: string
  onReviewSubmitted: () => void
}

export default function ReviewModal({
  open,
  onClose,
  meeting,
  currentUserId,
  onReviewSubmitted
}: ReviewModalProps) {
  const [rating, setRating] = useState(5)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const { user } = useAuth()
  const { userProfile } = useUserProfile()

  // Determine if current user is the client
  const isClient = meeting.professionalId !== currentUserId

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError('Please provide a comment with your review.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      // Determine who is being reviewed
      const revieweeId = isClient ? meeting.professionalId : meeting.clientId

      if (!revieweeId) {
        throw new Error('Could not determine reviewee')
      }

      // Construct reviewer name from profile data
      const reviewerName = userProfile?.firstName && userProfile?.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`
        : user?.displayName || 'Anonymous';

      // Create the review using the service (which uses the new subcollection structure)
      const reviewId = await createReview(revieweeId, {
        rating,
        comment: comment.trim(),
        reviewerId: currentUserId,
        reviewerName,
        reviewerPhotoUrl: userProfile?.profileImage || undefined,
        bookingId: meeting.id,
        serviceCategory: meeting.type,
        meetingDate: meeting.date,
        isVerified: true, // Since it's from an actual meeting
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isHidden: false,
        reportCount: 0
      })

      if (!reviewId) {
        throw new Error('Failed to create review')
      }

      // Mark review as submitted for this meeting
      const markResult = await markReviewSubmitted(meeting.id, currentUserId)

      if (!markResult) {
        console.warn('Failed to mark review as submitted, but review was created')
      }

      onReviewSubmitted()
    } catch (err) {
      console.error('Error submitting review:', err)
      setError('Failed to submit review. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!submitting) {
      setRating(5)
      setHoveredRating(0)
      setComment('')
      setError('')
      onClose()
    }
  }

  // Get initials from name
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    if (parts.length === 1 && parts[0].length > 0) return parts[0].substring(0, 2).toUpperCase()
    return 'U'
  }

  const displayRating = hoveredRating || rating

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[440px] overflow-hidden rounded-2xl relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded-full p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors z-10"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="p-6 md:p-8">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-full grid place-items-center mb-5"
                    style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                    </svg>
                  </div>

                  {/* Title */}
                  <h3 className="text-[22px] font-medium tracking-tight mb-5">Leave a <span className="font-serif italic font-normal">review</span></h3>

                  {/* Meeting info card */}
                  <div className="rounded-xl p-4 mb-6 flex items-center gap-3.5"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                    {meeting.imageUrl ? (
                      <img
                        src={meeting.imageUrl}
                        alt={meeting.name}
                        className="h-11 w-11 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-11 w-11 rounded-full shrink-0 grid place-items-center text-[13px] font-semibold"
                        style={{ background: 'rgba(255,255,255,0.08)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 25%)' }}>
                        {getInitials(meeting.name)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium truncate">{meeting.name}</div>
                      <div className="text-white/50 text-[12px] mt-0.5 truncate">
                        {meeting.location}{meeting.date && <> · {meeting.date}</>}
                      </div>
                    </div>
                  </div>

                  {/* Star rating */}
                  <div className="mb-6">
                    <label className="label">How would you rate this {isClient ? 'professional' : 'client'}?</label>
                    <div className="flex items-center gap-1.5 mt-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          disabled={submitting}
                          className="p-0.5 hover:scale-110 transition-transform disabled:cursor-not-allowed"
                        >
                          <svg width="28" height="28" viewBox="0 0 24 24"
                            fill={star <= displayRating ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={star <= displayRating ? 'text-amber-400' : 'text-white/20 hover:text-white/40'}
                            style={{ transition: 'color 0.15s' }}
                          >
                            <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                          </svg>
                        </button>
                      ))}
                      <span className="text-[12.5px] text-white/40 ml-2">
                        {displayRating} star{displayRating !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Comment */}
                  <div className="mb-5">
                    <label className="label">Share your experience</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      disabled={submitting}
                      rows={4}
                      placeholder={`Tell others about your experience with ${meeting.name}...`}
                      className="field min-h-[100px] resize-none mt-1.5"
                    />
                    <p className="text-[11px] text-white/30 mt-1.5">
                      Your review will be visible to other users.
                    </p>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="rounded-xl p-3.5 mb-5 flex items-start gap-2.5"
                      style={{ background: 'rgba(255,138,115,0.06)', border: '1px solid rgba(255,138,115,0.2)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      <p className="text-[12.5px] leading-relaxed" style={{ color: '#ff8a73' }}>{error}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleClose}
                      disabled={submitting}
                      className="btn btn-outline flex-1 justify-center !py-3"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !comment.trim()}
                      className="btn btn-primary flex-1 justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full"></span>
                          Submitting...
                        </span>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
                          Submit Review
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
